/* eslint-disable max-lines */
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { FC, KeyboardEvent, useEffect, useRef, useState } from "react";
import { cx } from "../../../utils/classnames";
import { MAX_TRIP_DURATION_MINUTES } from "../../../utils/tripDuration";
import InputWrapper from "./InputWrapper";

export const pad2 = (value: number) => String(value).padStart(2, "0");

// Hour lists for the segmented fields. Duration hours run to the cap in tripDuration.ts (96, four
// days) - the whole point of the rework is that a trip can be longer than a day, which no
// 00:00-23:59 time picker can express. Raising the cap there raises this list with it.
export const HOUR_OPTIONS_24 = Array.from({ length: 24 }, (_, h) => pad2(h));
export const HOUR_OPTIONS_DURATION = Array.from(
  { length: Math.floor(MAX_TRIP_DURATION_MINUTES / 60) + 1 },
  (_, h) => pad2(h)
);
export const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, m) => pad2(m));

type Part = "hours" | "minutes";

// Typed digits build on each other for this long ("3" then "1" lands on 31), then start over.
const TYPEAHEAD_MS = 800;

// One visual input box holding an hour and a minute picker. It has the ring and height of Input,
// and each half opens the same white list the SelectInput fields use (rounded, the picked row in
// the primary colour with a tick) instead of the browser's own select popup, so the length reads
// like the departure box beside it. Every value carries its unit, "05h" / "30m", so the two
// halves cannot be mistaken for a clock. Used for the trip's length, every stop's time after the
// departure, and the bulk editor's length.
const TimePartsField: FC<{
  id: string;
  label: string;
  hourOptions: string[];
  hours: string;
  minutes: string;
  onPartChange: (part: Part, value: string) => void;
  error?: string;
  // The unit printed after each value.
  units?: { hours: string; minutes: string };
}> = ({
  id,
  label,
  hourOptions,
  hours,
  minutes,
  onPartChange,
  error,
  units = { hours: "h", minutes: "m" }
}) => {
  const [open, setOpen] = useState<Part | null>(null);
  const [highlighted, setHighlighted] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // "top" on opening (the current value heads the list), "nearest" while moving through it.
  const scrollMode = useRef<"top" | "nearest">("top");
  const typed = useRef({ text: "", at: 0 });

  const optionsOf = (part: Part) => (part === "hours" ? hourOptions : MINUTE_OPTIONS);
  const valueOf = (part: Part) => (part === "hours" ? hours : minutes);
  const unitOf = (part: Part) => (part === "hours" ? units.hours : units.minutes);
  const nameOf = (part: Part) => (part === "hours" ? "orë" : "minuta");

  const openPart = (part: Part) => {
    scrollMode.current = "top";
    setOpen(part);
    setHighlighted(Math.max(0, optionsOf(part).indexOf(valueOf(part))));
  };
  const close = () => {
    setOpen(null);
    setHighlighted(-1);
  };
  const pick = (part: Part, value: string) => {
    onPartChange(part, value);
    close();
  };
  const highlight = (index: number) => {
    scrollMode.current = "nearest";
    setHighlighted(index);
  };

  // A click anywhere else closes the list, as it does on the SelectInput fields.
  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close();
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  // Keep the highlighted row in view. The list is the rows' offset parent, so offsetTop is
  // already measured inside it.
  useEffect(() => {
    if (!open || highlighted < 0) return;
    requestAnimationFrame(() => {
      const list = listRef.current;
      const row = list?.children[highlighted] as HTMLElement | undefined;
      if (!list || !row) return;
      const top = row.offsetTop;
      const bottom = top + row.offsetHeight;
      if (scrollMode.current === "top") list.scrollTop = top;
      else if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
    });
  }, [open, highlighted]);

  const onKeyDown = (part: Part) => (event: KeyboardEvent<HTMLButtonElement>) => {
    const options = optionsOf(part);
    const isOpen = open === part;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) return openPart(part);
      const step = event.key === "ArrowDown" ? 1 : -1;
      return highlight((highlighted + step + options.length) % options.length);
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!isOpen) return openPart(part);
      return highlighted >= 0 ? pick(part, options[highlighted]) : close();
    }
    if (event.key === "Escape" || event.key === "Tab") {
      if (isOpen) close();
      return undefined;
    }
    // Typed digits jump to the matching value, as the browser's own select does: picked at once
    // while the list is closed, highlighted while it is open so Enter confirms.
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      const now = Date.now();
      const text = (now - typed.current.at < TYPEAHEAD_MS ? typed.current.text : "") + event.key;
      typed.current = { text: text.slice(-2), at: now };
      const match = options.findIndex((option) => option.startsWith(typed.current.text));
      if (match < 0) return undefined;
      return isOpen ? highlight(match) : onPartChange(part, options[match]);
    }
    return undefined;
  };

  const renderHalf = (part: Part) => {
    const value = valueOf(part);
    const isOpen = open === part;
    return (
      <button
        type="button"
        id={part === "hours" ? id : undefined}
        className={cx([
          "h-9 flex-1 rounded-md text-sm text-center outline-none cursor-pointer focus-visible:bg-gray-100",
          isOpen && "bg-gray-100",
          !value && "text-gray-400"
        ])}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label} - ${nameOf(part)}`}
        onClick={() => (isOpen ? close() : openPart(part))}
        onKeyDown={onKeyDown(part)}
      >
        {value ? `${value}${unitOf(part)}` : "--"}
      </button>
    );
  };

  const renderList = (part: Part) => (
    <div
      ref={listRef}
      role="listbox"
      aria-label={`${label} - ${nameOf(part)}`}
      className={cx([
        "absolute z-[999] mt-2 max-h-80 w-1/2 min-w-[5.5rem] overflow-y-auto rounded-md border bg-white",
        part === "hours" ? "left-0" : "right-0"
      ])}
    >
      {optionsOf(part).map((option, index) => {
        const selected = option === valueOf(part);
        return (
          <div
            key={option}
            role="option"
            aria-selected={selected}
            className={cx([
              "flex cursor-pointer items-center justify-between px-3 py-2 text-sm rounded",
              highlighted === index && "bg-primary bg-opacity-5",
              selected && "text-primary",
              "hover:bg-primary hover:bg-opacity-5"
            ])}
            // Keep the focus on the half's button, so the keyboard still works after a click.
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => highlight(index)}
            onClick={() => pick(part, option)}
          >
            <span>{`${option}${unitOf(part)}`}</span>
            {selected && <CheckIcon className="w-4 h-4 text-primary shrink-0 ml-2" />}
          </div>
        );
      })}
    </div>
  );

  return (
    <InputWrapper id={id} label={label} error={error}>
      <div ref={rootRef} className="relative">
        <div
          className={cx([
            "flex items-center h-11 rounded-lg ring-1 px-1 transition-all duration-300 hover:ring-gray-500",
            error ? "ring-warning" : "ring-blue-stroke"
          ])}
        >
          {renderHalf("hours")}
          {renderHalf("minutes")}
          <ChevronDownIcon
            className={cx([
              "h-3 w-3 mx-1.5 shrink-0 transform duration-150",
              open ? "rotate-180" : "rotate-0"
            ])}
          />
        </div>
        {open ? renderList(open) : null}
      </div>
    </InputWrapper>
  );
};

export default TimePartsField;
